import { Eye, EyeOff, LogIn, X } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom'; // Import useSearchParams
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../api'; // Assuming authApi is exported from api.ts
import logoImage from '../assets/logo.png';
import ModalPortal from './ModalPortal';

// --- PasswordResetModal Component ---
interface PasswordResetModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialToken?: string; // Optional: for direct link access
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({ isOpen, onClose, initialToken }) => {
    const [step, setStep] = useState<'request' | 'reset'>(initialToken ? 'reset' : 'request');
    const [email, setEmail] = useState('');
    const [token, setToken] = useState(initialToken || '');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (initialToken) {
            setStep('reset');
            setToken(initialToken);
        } else {
            setStep('request');
            setToken('');
        }
        setEmail('');
        setNewPassword('');
        setConfirmNewPassword('');
        setError('');
        setSuccess('');
    }, [isOpen, initialToken]);


    const handleRequestReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await authApi.requestPasswordReset(email.trim());
            if (response.success) {
                setSuccess("Reset link sent to email if it exists in the system.");
            } else {
                setError(response.message || 'Failed to request password reset.');
            }
        } catch (err: any) {
            console.error('Request password reset error:', err);
            setError(err.response?.data?.message || 'Failed to request password reset. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        if (newPassword !== confirmNewPassword) {
            setError('New passwords do not match.');
            setLoading(false);
            return;
        }
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long.');
            setLoading(false);
            return;
        }

        try {
            const response = await authApi.resetPasswordWithToken(token.trim(), newPassword);
            if (response.success) {
                setSuccess(response.message + " You can now log in with your new password.");
                // Optionally close modal after successful reset
                setTimeout(() => {
                    onClose();
                }, 3000);
            } else {
                setError(response.message || 'Failed to reset password.');
            }
        } catch (err: any) {
            console.error('Reset password with token error:', err);
            setError(err.response?.data?.message || 'Failed to reset password. Please check your token and try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <ModalPortal>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500/75 p-4">
                <div className="w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg bg-white text-left shadow-xl">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                {step === 'request' ? 'Request Password Reset' : 'Reset Your Password'}
                            </h3>
                            <button
                                type="button"
                                className="text-gray-400 hover:text-gray-500"
                                onClick={onClose}
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
                                {error}
                            </div>
                        )}
                        {success && (
                            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md mb-4">
                                {success}
                            </div>
                        )}

                        {step === 'request' ? (
                            <form onSubmit={handleRequestReset} className="space-y-4">
                                <div>
                                    <label htmlFor="email" className="form-label">
                                        Email Address
                                    </label>
                                    <input
                                        id="email"
                                        type="email"
                                        required
                                        className="form-input"
                                        placeholder="Enter your registered email"
                                    value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setError('');
                                            setSuccess('');
                                        }}
                                        disabled={loading}
                                    />
                                </div>
                                <div className="flex justify-end">
                                    <button
                                        type="submit"
                                        className="btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <div className="flex items-center">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Sending...
                                            </div>
                                        ) : (
                                            'Send Reset Link'
                                        )}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <form onSubmit={handleResetPassword} className="space-y-4">
                                <div>
                                    <label htmlFor="token" className="form-label">
                                        Reset Token
                                    </label>
                                    <input
                                        id="token"
                                        type="text"
                                        required
                                        className="form-input"
                                        placeholder="Enter the token from your email"
                                        value={token}
                                        onChange={(e) => {
                                            setToken(e.target.value);
                                            setError('');
                                            setSuccess('');
                                        }}
                                        disabled={loading || !!initialToken} // Disable if initialToken is provided
                                    />
                                </div>
                                <div>
                                    <label htmlFor="new-password" className="form-label">
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="new-password"
                                            type={showPassword ? 'text' : 'password'}
                                            required
                                            className="form-input pr-10"
                                            placeholder="Enter new password (min 8 characters)"
                                            value={newPassword}
                                            onChange={(e) => {
                                                setNewPassword(e.target.value);
                                                setError('');
                                                setSuccess('');
                                            }}
                                            disabled={loading}
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-4 w-4 text-gray-400" />
                                            ) : (
                                                <Eye className="h-4 w-4 text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="confirm-new-password" className="form-label">
                                        Confirm New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="confirm-new-password"
                                            type={showPassword ? 'text' : 'password'}
                                            required
                                            className="form-input pr-10"
                                            placeholder="Confirm your new password"
                                            value={confirmNewPassword}
                                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                                            disabled={loading}
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-4 w-4 text-gray-400" />
                                            ) : (
                                                <Eye className="h-4 w-4 text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <div className="flex justify-end">
                                    <button
                                        type="submit"
                                        className="btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <div className="flex items-center">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Resetting...
                                            </div>
                                        ) : (
                                            'Reset Password'
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
};


// --- Login Component ---
const Login: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetTokenFromUrl, setResetTokenFromUrl] = useState<string | undefined>(undefined);

    const { login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams(); // Hook to read URL query parameters

    useEffect(() => {
        const token = searchParams.get('token');
        if (token) {
            setResetTokenFromUrl(token);
            setShowResetModal(true); // Open modal if token is present in URL
        }
    }, [searchParams]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await login(username, password);
            navigate('/dashboard');
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <div className="mx-auto flex items-center justify-center">
                    <img
                  src={logoImage}
                  alt="Bajaj Finserv Logo"
                  className="h-16 w-auto object-contain"
                />
                    </div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Bajaj SkillServ Centre Management Training Portal
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Sign in to your account
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                            {error}
                        </div>
                    )}

                    <div className="space-y-4">
                        <div>
                            <label htmlFor="username" className="form-label">
                                Username or Email
                            </label>
                            <input
                                id="username"
                                name="username"
                                type="text"
                                required
                                className="form-input"
                                placeholder="Enter your username or email"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="form-label">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    required
                                    className="form-input pr-10"
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4 text-gray-400" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-gray-400" />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="text-sm">
                            <button
                                type="button"
                                onClick={() => setShowResetModal(true)}
                                className="font-medium text-primary-600 hover:text-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                Forgot your password?
                            </button>
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Signing in...
                                </div>
                            ) : (
                                <>
                                    <LogIn className="h-4 w-4 mr-2" />
                                    Sign in
                                </>
                            )}
                        </button>
                    </div>

                </form>
            </div>

            {/* Password Reset Modal */}
            <PasswordResetModal
                isOpen={showResetModal}
                onClose={() => setShowResetModal(false)}
                initialToken={resetTokenFromUrl}
            />
        </div>
    );
};

export default Login;
